import { ProjectOutlined } from "@ant-design/icons"
import { Alert, Button, Divider, Flex, Form, Switch, Typography } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import InputNumber from "../../../../components/Input/InputNumber.tsx"
import { unwrapEnvelope } from "../../../../utils/api/unwrap-envelope.ts"
import { showResponseMessage } from "../../../../utils/handlers/show-response-message.ts"
import {
  type ProjectListSettings,
  getProjectListSettings,
  updateProjectListSettings
} from "../../api/project-list-settings.services.ts"
import { SettingsCard } from "../common/SettingsCard.tsx"

const { Text } = Typography

// The backend's own defaults, shown only until the stored values arrive.
export const PROJECT_LIST_DEFAULTS: ProjectListSettings = {
  project_due_soon_days: 5,
  project_closing_soon_days: 14,
  project_stale_days: 14,
  project_attention_late: true,
  project_attention_unscheduled: true,
  project_attention_unassigned: true,
  project_attention_stale: true,
  project_attention_over_budget: true
}

const DAY_FIELDS = [
  "project_due_soon_days",
  "project_closing_soon_days",
  "project_stale_days"
] as const

const REASON_FIELDS = [
  "project_attention_late",
  "project_attention_unscheduled",
  "project_attention_unassigned",
  "project_attention_stale",
  "project_attention_over_budget"
] as const

const changedFields = (
  values: ProjectListSettings,
  saved: ProjectListSettings
): Partial<ProjectListSettings> =>
  Object.fromEntries(
    Object.entries(values).filter(
      ([key, value]) => saved[key as keyof ProjectListSettings] !== value
    )
  )

export function ProjectListSettingsCard() {
  const { t } = useTranslation()
  const [settings, setSettings] = useState<ProjectListSettings | null>(null)
  const [saving, setSaving] = useState(false)
  // Locked until the stored values arrive: saving untouched defaults over a
  // failed load would overwrite the company's real thresholds.
  const [loadState, setLoadState] = useState<"loading" | "loaded" | "error">(
    "loading"
  )
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [form] = Form.useForm<ProjectListSettings>()
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoadState("loading")
    void getProjectListSettings().then((response) => {
      if (cancelled) return
      if (response?.ok && response.data) {
        setSettings(response.data)
        setLoadState("loaded")
      } else {
        setLoadState("error")
      }
    })
    return () => {
      cancelled = true
    }
  }, [loadAttempt])

  const initialValues = useMemo<ProjectListSettings>(
    () => ({ ...PROJECT_LIST_DEFAULTS, ...settings }),
    [settings]
  )

  useEffect(() => {
    if (!dirty) form.setFieldsValue(initialValues)
  }, [form, initialValues, dirty])

  const handleFinish = async (values: ProjectListSettings) => {
    const patch = changedFields(values, initialValues)
    if (!Object.keys(patch).length) {
      setDirty(false)
      return
    }
    setSaving(true)
    try {
      const response = await updateProjectListSettings(patch)
      if (response?.ok && response.data) {
        setSettings(unwrapEnvelope<ProjectListSettings>(response.data))
        setDirty(false)
        showResponseMessage(
          response,
          t("settingsConfiguration.projectList.saved")
        )
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsCard
      iconWrapperClassName="bg-teal-100"
      icon={<ProjectOutlined className="text-teal-700" />}
      title={t("settingsConfiguration.projectList.title")}
      subtitle={t("settingsConfiguration.projectList.subtitle")}
    >
      {loadState === "error" && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message={t("settingsConfiguration.projectList.loadError")}
          action={
            <Button size="small" onClick={() => setLoadAttempt((n) => n + 1)}>
              {t("settingsConfiguration.projectList.retry")}
            </Button>
          }
        />
      )}
      <Form
        form={form}
        layout="vertical"
        className="with-default-error"
        initialValues={initialValues}
        disabled={saving || loadState !== "loaded"}
        onValuesChange={() => setDirty(true)}
        onFinish={handleFinish}
      >
        <Text strong>{t("settingsConfiguration.projectList.thresholds")}</Text>
        <div className="mt-3 grid gap-x-6 md:grid-cols-3">
          {DAY_FIELDS.map((name) => (
            <Form.Item
              key={name}
              name={name}
              label={t(
                `settingsConfiguration.projectList.fields.${name}.label`
              )}
              extra={t(`settingsConfiguration.projectList.fields.${name}.hint`)}
              rules={[
                {
                  required: true,
                  message: t("settingsConfiguration.projectList.daysRequired")
                },
                {
                  type: "number",
                  min: 1,
                  max: 365,
                  message: t("settingsConfiguration.projectList.daysRange")
                }
              ]}
            >
              <InputNumber
                min={1}
                max={365}
                precision={0}
                className="w-full"
                addonAfter={t("settingsConfiguration.projectList.days")}
              />
            </Form.Item>
          ))}
        </div>

        <Divider className="my-2" />
        <Text strong>{t("settingsConfiguration.projectList.attention")}</Text>
        <Text type="secondary" className="block mb-3">
          {t("settingsConfiguration.projectList.attentionHint")}
        </Text>
        <Flex vertical gap={4}>
          {REASON_FIELDS.map((name) => (
            <Form.Item
              key={name}
              name={name}
              valuePropName="checked"
              className="mb-2"
            >
              <SwitchRow
                label={t(
                  `settingsConfiguration.projectList.fields.${name}.label`
                )}
                hint={t(
                  `settingsConfiguration.projectList.fields.${name}.hint`
                )}
              />
            </Form.Item>
          ))}
        </Flex>

        <Flex justify="end">
          <Button
            type="primary"
            htmlType="submit"
            loading={saving}
            disabled={!dirty || loadState !== "loaded"}
          >
            {t("settingsConfiguration.projectList.save")}
          </Button>
        </Flex>
      </Form>
    </SettingsCard>
  )
}

interface SwitchRowProps {
  label: string
  hint: string
  checked?: boolean
  disabled?: boolean
  onChange?: (checked: boolean) => void
}

/** A switch with its label beside it, so the label is its accessible name. */
function SwitchRow({
  label,
  hint,
  checked,
  disabled,
  onChange
}: SwitchRowProps) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="mt-0.5 shrink-0"
      />
      <span className="flex flex-col">
        <span className="text-slate-800">{label}</span>
        <span className="text-[13px] text-slate-600">{hint}</span>
      </span>
    </label>
  )
}
